import { importLibrary, setOptions } from "@googlemaps/js-api-loader"

// `@googlemaps/js-api-loader` v2 dropped the old `new Loader(...).importLibrary()`
// instance API in favour of two module-level functions (`setOptions` +
// `importLibrary`) — the `Loader` class the plan's own brief sketched still
// exists but is now a deprecated no-op stub with no typed methods, so this
// module is written against the real v2 functional API instead. `setOptions`
// must run exactly once; `importLibrary` is idempotent per-library but we
// still memoize the resulting promise so a second `loadGoogleMaps()` call
// (e.g. a second `<MapCanvas>` mount) doesn't kick off redundant loads.
let mapsPromise: Promise<typeof google.maps> | null = null
const authFailureListeners = new Set<() => void>()

declare global {
  interface Window {
    // Google's own official hook, called when it detects the key is
    // invalid/unauthorized for this referrer — sometimes only once it tries
    // to authenticate real tile/data requests, well after the bootstrap
    // script (and this module's own promise) has already resolved
    // successfully. Left undefined, Google shows its own raw, unstyled
    // error dialog instead; defining it is the only way to catch this
    // failure ourselves and show ``MapCanvas``'s usual provider-error state.
    gm_authFailure?: () => void
  }
}

function notifyAuthFailure(): void {
  authFailureListeners.forEach((listener) => listener())
}

// `MapCanvas` subscribes here in addition to `.catch()`ing the promise
// below, since a `gm_authFailure` call can arrive after that promise has
// already settled successfully.
export const onMapsAuthFailure = (listener: () => void): (() => void) => {
  authFailureListeners.add(listener)
  return () => authFailureListeners.delete(listener)
}

// `renderOverlays` (map/markers.ts) constructs
// `google.maps.marker.AdvancedMarkerElement` directly off the ambient global,
// so the "marker" library has to be actually loaded — not just listed as a
// `setOptions({ libraries })` preload hint, which only tells the bootstrap
// loader to start fetching it earlier and does not guarantee it has resolved
// onto `google.maps.marker` by the time this promise settles.
//
// "visualization" was briefly added here for Analytics' Record Distribution
// heatmap, then removed the same session: Google deprecated AND fully
// removed `visualization.HeatmapLayer` from the Maps JavaScript API as of
// v3.65 — confirmed live ("The Heatmap Layer functionality ... is no longer
// available", thrown at runtime, not just a stale type). `RecordDistribution
// Heatmap.tsx` draws the same record-density signal with plain
// `google.maps.Circle` instead, which needs no extra library.
export const loadGoogleMaps = (): Promise<typeof google.maps> => {
  if (!mapsPromise) {
    window.gm_authFailure = notifyAuthFailure
    setOptions({
      key: import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY ?? "",
      v: "weekly",
      libraries: ["maps", "marker"]
    })
    mapsPromise = Promise.all([
      importLibrary("maps"),
      importLibrary("marker")
    ]).then(() => google.maps)
  }
  return mapsPromise
}

export const __resetLoaderForTests = () => {
  mapsPromise = null
  authFailureListeners.clear()
  delete window.gm_authFailure
}
